/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { Spinner, SPINNER_TYPE_REGULAR, Table, TableCell, tableDefaults, TableProps } from '@squiz/sds';
import {
  GetFileStoreMappingListResponse,
  useFileStoreMappings,
} from 'apps/digital-asset-management/src/Services/file-store-mappings';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CONFIGURATION_STRINGS } from '../../../../Constants/Strings.constants';
import { AddRouteButton } from '../AddRouteButton/AddRouteButton';
import { RouteActionMenu } from '../RouteActionMenu/RouteActionMenu';
import { RouteListProps } from './RouteList.props';

export const RouteList = ({ siteUrl }: RouteListProps): JSX.Element => {
  const { t } = useTranslation();

  // File store mappings
  const { getMappingList } = useFileStoreMappings();

  // State
  const [cacheKey, setCacheKey] = useState<string>('');
  const [mappings, setMappings] = useState<GetFileStoreMappingListResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: tableDefaults.perPageDefault });

  // Labels
  const labels = useMemo(() => {
    return {
      noRoutes: t(CONFIGURATION_STRINGS.noRoutes),
      repositoryPath: t(CONFIGURATION_STRINGS.repositoryPath),
      siteUrl: t(CONFIGURATION_STRINGS.siteUrl),
    };
  }, [t]);

  // Get the mappings
  const fetchMappings = useCallback(async (): Promise<void> => {
    const mappings = await getMappingList({ filterAddress: siteUrl, pageLimit: 1000 });

    setMappings(mappings);
    setIsLoading(false);
  }, [getMappingList, setIsLoading, setMappings, siteUrl]);

  // Initial fetch
  useEffect(() => {
    if (!mappings) {
      fetchMappings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappings]);

  // Re-fetch when cache key is updated
  useEffect(() => {
    if (cacheKey.length) {
      fetchMappings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  const tableColumns: Array<TableCell> = [
    {
      content: labels.siteUrl,
      key: 'siteUrl',
    },
    {
      content: labels.repositoryPath,
      key: 'repositoryPath',
    },
    {
      actionable: true,
      content: '',
      key: 'actions',
    },
  ];

  const tableRows: Array<{}> = useMemo(() => {
    if (!mappings || !mappings.data) {
      return [];
    }

    return mappings.data.map((mapping, i) => {
      const { address, source, id } = mapping;

      // The last item per page should always have the action menu open upwards
      const shouldActionMenuBeUp =
        i % pagination.pageSize === pagination.pageSize - 1 ||
        (i === mappings.data.length - 1 && mappings.data.length < pagination.pageSize);

      return {
        _config: {
          classes: 'route-list__row',
        },
        actions: (
          <RouteActionMenu {...mapping} clearCache={setCacheKey} dropdownUp={shouldActionMenuBeUp} siteUrl={siteUrl} />
        ),
        key: `mapping--${id}`,
        repositoryPath: source,
        siteUrl: address,
      };
    });
  }, [mappings, pagination.pageSize, siteUrl]);

  if (isLoading) {
    return (
      <div className="loading">
        <Spinner type={SPINNER_TYPE_REGULAR} />
      </div>
    );
  }

  const tableProps: TableProps = {
    classes: 'route-list__table',
    emptyText: labels.noRoutes,
    isPaginated: true,
    isSlim: true,
    pagination,
    setPagination,
    tableContent: {
      columns: tableColumns,
      rows: tableRows,
    },
  };

  return (
    <>
      <div className="route-list__header">
        <AddRouteButton clearCache={setCacheKey} siteUrl={siteUrl} />
      </div>
      <Table {...tableProps} />
    </>
  );
};
